import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  setPanes,
  currentFieldGroupSelected,
  setFormServerErrors,
  loadPage,
  loadDataset,
  loadEntity,
  createEntity,
  saveEntity,
  removeEntityFromUI,
  deleteEntity,
  testExpression,
  insertCodeSnippet,
  insertFunction,
  entityCloned,
  disposePage,
  setPageValue,
} from "../../redux/slices/expressionspageslice";
import {
  setCurentPage,
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../redux/slices/currentpageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import {
  cloneExpression,
  initEntityCloner,
} from "../../redux/slices/entityclonerslice";
import {
  notificationDurationShort,
  notificationDurationMiddle,
  notificationDurationLong,
  deepCompare,
} from "../../lib/utils";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Page } from "../page";
import { EntityCloner } from "../../components/entitycloner";
import { ExpressionsPageBody } from "./expressionspagebody";
import { formValidator } from "./expressionspageformvalidator";
import { ReactComponent as SqlIcon } from "../../icons/sql.svg";
import { ReactComponent as DatabaseIcon } from "../../icons/database.svg";
import { ReactComponent as AggregateIcon } from "../../icons/aggregate.svg";
import { ReactComponent as FunctionsIcon } from "../../icons/functions.svg";
import { ReactComponent as HelpIcon } from "../../icons/help.svg";
import { ReactComponent as ConditionIcon } from "../../icons/diamond.svg";
import { ReactComponent as LockIcon } from "../../icons/folderlock.svg";
import { ReactComponent as GroupIcon } from "../../icons/layergroup.svg";
import { helpersMethods } from "./helpers";
import { BLOCK_DIRTY_MESSAGE } from "../../settings";
import "./expressionspage.css";
import { expressionMaxReached } from "../../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const ExpressionsPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.expressionsPage);
  const currentPageState = useSelector((state) => state.currentPage);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const introItems = useMemo(
    () => [
      {
        color: currentTheme.colorVariant1,
        icon: SqlIcon,
        width: 64,
        height: 32,
        content: t("expressions.intro_sql"),
      },
      {
        color: currentTheme.colorVariant2,
        icon: DatabaseIcon,
        width: 90,
        height: 32,
        content: t("expressions.intro_database"),
      },
      {
        color: currentTheme.colorVariant2,
        icon: AggregateIcon,
        width: 100,
        height: 32,
        content: t("expressions.intro_aggregate"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: FunctionsIcon,
        width: 80,
        height: 32,
        content: t("expressions.intro_functions"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: ConditionIcon,
        width: 80,
        height: 32,
        content: t("expressions.intro_condition"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: LockIcon,
        width: 80,
        height: 32,
        content: t("expressions.intro_lock"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: GroupIcon,
        width: 80,
        height: 32,
        content: t("expressions.intro_group"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: HelpIcon,
        width: 90,
        height: 32,
        content: t("expressions.intro_help"),
      },
    ],
    [t, currentTheme]
  );

  const helpItemsFixed = useMemo(
    () => [
      {
        color: currentTheme.colorVariant1,
        icon: SqlIcon,
        width: 42,
        height: 42,
        content: t("expressions.help_sql"),
      },
      {
        color: currentTheme.colorVariant2,
        icon: DatabaseIcon,
        width: 42,
        height: 42,
        content: t("expressions.help_database"),
      },
      {
        color: currentTheme.colorVariant2,
        icon: AggregateIcon,
        width: 100,
        height: 64,
        content: t("expressions.help_aggregate"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: FunctionsIcon,
        width: 52,
        height: 52,
        content: t("expressions.help_functions"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: HelpIcon,
        width: 52,
        height: 52,
        content: t("expressions.help_help"),
      },
    ],
    [t, currentTheme]
  );
  let helpItemsHelper = useRef(helpersMethods);

  const [helpItems, setHelpItems] = useState(helpItemsHelper);
 
  const [numericIsVisible, setNumericIsVisible] = useState(false);
  const [textIsVisible, setTextIsVisible] = useState(false);
  const [dateIsVisible, setDateIsVisible] = useState(false);
  const [dateTimeIsVisible, setDateTimeIsVisible] = useState(false);

  let currentPageId = "expressionsPage"; 
  const handleOnDataTypeChange = (e) => {
    if (e.value == 2 || e.value == 3 || e.value == 4 || e.value == 5) {
      const normalized = Array.isArray(helpItemsHelper)
        ? helpItemsHelper
        : helpItemsHelper?.current || [];

      setHelpItems(normalized.find((x) => x.dataType === e.value)?.items || []);
    } else setHelpItems(helpItemsFixed);

    setNumericIsVisible(false);
    setTextIsVisible(false);
    setDateIsVisible(false);
    setDateTimeIsVisible(false);
  };

  const handleOnHelpersGroupToggle = (id) => {
    if (id == 2) setNumericIsVisible(!numericIsVisible);
    else if (id == 3) setTextIsVisible(!textIsVisible);
    else if (id == 4) setDateIsVisible(!dateIsVisible);
    else if (id == 5) setDateTimeIsVisible(!dateTimeIsVisible);
  };

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let isDirty = !deepCompare(
      pageState.formInitialValues,
      pageState.formValues
    );
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("expressions.loading_error"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("expressions.errormessage"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    setCurrentPage({ id: "", isDirty: false });
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  /*panel bar*/
  const dataSetsPanelRef = useRef(null);

  //on data set change
  function handleOnDatasetChange(e) {
    const selectedItemId = e.value;

    if (blockNavigationIfDirty() == true) return;

    dispatch(loadDataset(selectedItemId, t)).then(
      (response) => { 
        setHelpItems(helpItemsFixed);
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("expressions.errormessage")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("expressions.errormessage");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  }

  //field group change
  const handleOnCurrentFieldGroupChange = (e) => {
    dispatch(currentFieldGroupSelected(e.value));
  };

  /*panel bar*/

  /*toolbar*/
  const handleOnNew = (e) => {
    if (!currentUser.IsAuthenticated || expressionMaxReached(currentUser)) {
      dispatch(
        addNotification({
          text: t("expressions.authorization_error"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
      return;
    }

    if (blockNavigationIfDirty() == true) return;

    dispatch(createEntity());
    setHelpItems(helpItemsFixed);
  };

  const handleOnTestExpression = (e) => {
    let body = pageState.formValues["Body"];
    if (!body || body.trim() == "") {
      dispatch(
        addNotification({
          text: t("expressions.expression_error"),
          type: "success",
          closable: true,
          duration: notificationDurationLong,
          position: "topRight",
        })
      );
      return;
    }

    dispatch(testExpression(t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("expressions.errormessage")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("expressions.errormessage");
          dispatch(setFormServerErrors([errorMessage]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleOnSave = (e) => {
    if (pageState.mode == 1) {
      //create
      if (!currentUser.IsAuthenticated || expressionMaxReached(currentUser)) {
        dispatch(
          addNotification({
            text: t("expressions.expression_error2"),
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        return;
      }
    }

    //make sure no client errors; if so return
    const isValidForm = formValidator(pageState.formValues) == undefined;
    if (!isValidForm) {
      dispatch(
        addNotification({
          text: t("expressions.invalid_info"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
      return;
    }

    //no client errors => proceed
    dispatch(saveEntity(t)).then(
      (response) => {
        dispatch(setCurrentPageIsDirty(false));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("expressions.errormessage")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
            if (payload.data.ErrorCodes.EntityDoesNotExist) {
              dispatch(removeEntityFromUI(pageState.formValues.Id));
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: notificationDurationMiddle,
                  position: "topRight",
                })
              );
            } else dispatch(setFormServerErrors(errorMessages));
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("expressions.errormessage");
          dispatch(setFormServerErrors([errorMessage]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleOnClone = (e) => {
    dispatch(
      initEntityCloner({ title: pageState.formValues.Title, navigate: true })
    );

    dispatch(
      setAppDialog({
        isVisible: true,
        closable: true,
        title: t("expressions.clone_title"),
        className: "",
        content: <EntityCloner className="" />,
        actions: {
          yesButton: {
            title: t("expressions.clone"),
            handler: (e) => {
              dispatch(cloneExpression(t)).then(
                (response) => {
                  dispatch(hideAppDialog());

                  dispatch(
                    addNotification({
                      text: t("expressions.clone_success"),
                      type: "success",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    })
                  );

                  dispatch(
                    entityCloned({
                      id: response.id,
                      title: response.title,
                      navigate: response.navigate,
                    })
                  );
                },
                (err) => {
                  if (err == "invalidData") {
                    //show notification that title is required
                    dispatch(
                      addNotification({
                        text: t("expressions.clone_expression"),
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                    return;
                  } else if (err == "ignore") {
                    //in this case the user clicked clone while it's cloning
                    //just do nothing
                    return;
                  } else {
                    const handleResponseReceived = (
                      state,
                      dispatch,
                      navigate,
                      payload
                    ) => {
                      let errorMessages = payload?.data?.ErrorMessages;
                      let errorMessage = t("expressions.errormessage");

                      if (errorMessages || errorMessages.length > 0) {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
                        if (payload.data.ErrorCodes.TitleExists)
                          errorMessage = errorMessages[0];
                      }

                      dispatch(
                        addNotification({
                          text: errorMessage,
                          type: "error",
                          closable: true,
                          duration: notificationDurationMiddle,
                          position: "topRight",
                        })
                      );
                    };

                    const handleUnhandled = (
                      state,
                      dispatch,
                      navigate,
                      payload
                    ) => {
                      dispatch(
                        addNotification({
                          text: t("expressions.errormessage"),
                          type: "error",
                          closable: true,
                          duration: notificationDurationMiddle,
                          position: "topRight",
                        })
                      );
                    };

                    apiErrorsHandler({}, dispatch, null, err, null, {
                      responseReceived: handleResponseReceived,
                      unhandled: handleUnhandled,
                    });

                    return Promise.resolve();
                  }
                }
              );
            },
          },
          noButton: {
            title: t("expressions.cancel"),
          },
        },
      })
    );
  };

  const handleOnInsertCodeSnippet = (formRenderProps, id) => {
    dispatch(insertCodeSnippet({ formRenderProps, id }));
  };

  const handleOnInsertFunction = (formRenderProps, func) => {
    dispatch(insertFunction({ formRenderProps, func }));
  };

  const handleOnErrorbarClose = (e) => {
    dispatch(setFormServerErrors([]));
  };

  /*toolbar*/

  /*delete expression*/
  const handleOnDelete = (id) => {
    //id is zero if deleted from toolbar and > 0 if deleted from expressions list
    //if id is zero and mode !=2 => must not happen
    if (!id && pageState.mode != 2) return;

    let isCurrentEntity = false;

    if (!id) {
      //deleted from the toolbar => it's the current entity and mode is 2
      id = pageState.formValues.Id;
      isCurrentEntity = true;
    } else if (pageState.formValues?.Id && pageState.formValues.Id == id)
      isCurrentEntity = true;

    if (isCurrentEntity) {
      //if contains dirty => block
      if (blockNavigationIfDirty() == true) return;
    }

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "expressions-page-are-you-sure",
        title: t("expressions.warning"),
        content: t("expressions.delete"),
        actions: {
          yesButton: {
            title: t("expressions.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(id, t)).then(
                (response) => {
                  dispatch(setProcessing({ processing: "" }));

                  if (isCurrentEntity)
                    //doesn't work because of closure
                    setHelpItems(helpItemsFixed);
                },
                (err) => {
                  dispatch(setProcessing({ processing: "" }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("expressions.errormessage")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(removeEntityFromUI(id));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload
                  ) => {
                    let errorMessage = t("expressions.errormessage");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      })
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                }
              );
            },
          },
          noButton: { title: t("expressions.no") },
        },
      })
    );
  };
  /*delete expression*/

  /*view/edit expression*/
  const handleOnEntitySelected = (expressionId, item) => {
    if (blockNavigationIfDirty() == true) return;

    dispatch(loadEntity(expressionId, t)).then(
      (response) => {
        let dataTypeId = 0;
        if (
          response &&
          response.data &&
          response.data.Data &&
          response.data.Data.DataTypeId
        ) {
          dataTypeId = response.data.Data.DataTypeId;

          try {
            if (
              dataTypeId == 2 ||
              dataTypeId == 3 ||
              dataTypeId == 4 ||
              dataTypeId == 5
            ) {
              const normalized = Array.isArray(helpItemsHelper)
                ? helpItemsHelper
                : helpItemsHelper?.current || [];

              setHelpItems(
                normalized.find((x) => x.dataType === dataTypeId)?.items || []
              );
            } else setHelpItems(helpItemsFixed);
          } catch (e) {}

          setNumericIsVisible(false);
          setTextIsVisible(false);
          setDateIsVisible(false);
          setDateTimeIsVisible(false);
        }
      },
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("expressions.errormessage")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist)
              dispatch(removeEntityFromUI(expressionId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("expressions.errormessage");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };
  /*view/edit expression*/
  //tools visible/hidden
  const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "84%",
            max: "78%",
            collapsible: false,
          },
          {
            size: "18%",
            min: "16%",
            max: "22%",
            collapsible: false,
          },
        ])
      );
    else
      dispatch(
        setPanes([
          {
            size: "20%",
            min: "18%",
            max: "25%",
            collapsible: false,
          },
          {
            min: "53%",
            max: "66%",
            collapsible: false,
          },
          {
            size: "18%",
            min: "16%",
            max: "22%",
            collapsible: false,
          },
        ])
      );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
  };

  return (
    <Fragment>
      <Page
        id="expressionsPage"
        className="expressions-page"
        title={t("expressions.title")}
        animation={props.animation}
      >
        <ExpressionsPageBody
          onSplitterChange={handleOnSplitterChange}
          onToolsToggle={handleOnToolsToggle}
          dataSetsPanelRef={dataSetsPanelRef}
          onDatasetChange={handleOnDatasetChange}
          onCurrentFieldGroupChange={handleOnCurrentFieldGroupChange}
          onNew={handleOnNew}
          onTestExpression={handleOnTestExpression}
          onSave={handleOnSave}
          onClone={handleOnClone}
          onDelete={handleOnDelete}
          onErrorbarClose={handleOnErrorbarClose}
          onInsertCodeSnippet={handleOnInsertCodeSnippet}
          onInsertFunction={handleOnInsertFunction}
          onEntitySelected={handleOnEntitySelected}
          onExpressionDoubleClick={handleOnEntitySelected}
          onDataTypeChange={handleOnDataTypeChange}
          onHelpersGroupToggle={handleOnHelpersGroupToggle}
          introItems={introItems}
          helpItems={helpItems}
          numericIsVisible={numericIsVisible}
          textIsVisible={textIsVisible}
          dateIsVisible={dateIsVisible}
          dateTimeIsVisible={dateTimeIsVisible}
          updateDirtyState={updateDirtyState}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .expressions-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .expressions-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .expressions-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .expressions-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .expressions-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `}
      </style>
    </Fragment>
  );
};

//export const ExpressionsPage = {Component: Expressions};
